import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getBracket, generateBracket } from '../services/bracket-api.js';
import { getCompetitionDetails } from '../services/public-api.js';
import { listTeams } from '../services/organizer-api.js';
import Loading from '../components/common/Loading.jsx';
import ErrorMessage from '../components/common/ErrorMessage.jsx';
import Button from '../components/common/Button.jsx';
import { useAuth } from '../hooks/useAuth.js';
import './BracketPage.css';

/**
 * BracketPage
 * Displays a visual single-elimination knockout bracket.
 * Organizers can generate the bracket from this page.
 */
export default function BracketPage() {
  const { competitionId } = useParams();
  const { isOrganizer } = useAuth();

  const [bracket, setBracket] = useState(null);
  const [competition, setCompetition] = useState(null);
  const [teamMap, setTeamMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, [competitionId]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [comp, bracketData] = await Promise.allSettled([
        getCompetitionDetails(competitionId),
        getBracket(competitionId),
      ]);

      if (comp.status === 'fulfilled') setCompetition(comp.value);

      if (bracketData.status === 'fulfilled') {
        setBracket(bracketData.value);
        // Load team names
        await loadTeamNames(bracketData.value);
      }
    } catch (err) {
      setError(err.message || 'Failed to load bracket');
    } finally {
      setLoading(false);
    }
  };

  const loadTeamNames = async (bracketData) => {
    const teamIds = new Set();
    bracketData.matches.forEach(m => {
      if (m.team1Id) teamIds.add(m.team1Id);
      if (m.team2Id) teamIds.add(m.team2Id);
    });

    // Try to resolve team names from public API
    try {
      const response = await listTeams(competitionId);
      const teams = response?.teams || response || [];
      const map = {};
      teams.forEach(t => { map[t.teamId] = t.name; });
      setTeamMap(map);
    } catch {
      // Non-fatal — IDs will show if names unavailable
    }
  };

  const handleGenerate = async () => {
    if (!window.confirm('Generate a random draw bracket for this competition? This cannot be undone.')) return;
    setGenerating(true);
    setError(null);
    try {
      const newBracket = await generateBracket(competitionId);
      setBracket(newBracket);
      await loadTeamNames(newBracket);
    } catch (err) {
      setError(err.message || 'Failed to generate bracket');
    } finally {
      setGenerating(false);
    }
  };

  const teamName = (id) => {
    if (!id) return 'BYE';
    return teamMap[id] || id;
  };

  // Group matches by round
  const rounds = bracket
    ? Array.from({ length: bracket.totalRounds }, (_, i) => ({
        roundNumber: i + 1,
        matches: bracket.matches
          .filter(m => m.round === i + 1)
          .sort((a, b) => a.position - b.position),
      }))
    : [];

  const roundLabel = (roundNum, total) => {
    if (roundNum === total) return 'Final';
    if (roundNum === total - 1) return 'Semi-Finals';
    if (roundNum === total - 2) return 'Quarter-Finals';
    return `Round ${roundNum}`;
  };

  if (loading) return <Loading />;

  return (
    <div className="bracket-page">
      <div className="bracket-header">
        <Link to={`/competitions/${competitionId}`} className="back-link">← Back to Competition</Link>
        <h1>{competition?.name || 'Competition'} — Bracket</h1>
        {competition?.competitionType === 'knockout' && isOrganizer && !bracket && (
          <Button
            onClick={handleGenerate}
            disabled={generating}
            variant="primary"
            className="generate-btn"
          >
            {generating ? 'Generating…' : '🎱 Generate Bracket Draw'}
          </Button>
        )}
      </div>

      {error && <ErrorMessage message={error} />}

      {!bracket && !error && (
        <div className="no-bracket">
          <p>No bracket has been generated yet for this competition.</p>
          {isOrganizer && (
            <p>As the organiser, you can generate the bracket once all teams are registered and approved.</p>
          )}
        </div>
      )}

      {bracket && (
        <div className="bracket-container">
          <div className="bracket-grid" style={{ '--rounds': bracket.totalRounds }}>
            {rounds.map(({ roundNumber, matches }) => (
              <div key={roundNumber} className="bracket-round">
                <div className="round-label">
                  {roundLabel(roundNumber, bracket.totalRounds)}
                </div>
                <div className="round-matches">
                  {matches.map(m => (
                    <div
                      key={m.bracketMatchId}
                      className={`bracket-match ${m.winnerId ? 'has-winner' : ''}`}
                    >
                      <div className={`bracket-team ${m.winnerId === m.team1Id ? 'winner' : ''} ${!m.team1Id ? 'bye' : ''}`}>
                        <span className="team-name">{teamName(m.team1Id)}</span>
                        {m.matchId && m.winnerId === m.team1Id && <span className="win-badge">✓</span>}
                      </div>
                      <div className="bracket-vs">vs</div>
                      <div className={`bracket-team ${m.winnerId === m.team2Id ? 'winner' : ''} ${!m.team2Id ? 'bye' : ''}`}>
                        <span className="team-name">{teamName(m.team2Id)}</span>
                        {m.matchId && m.winnerId === m.team2Id && <span className="win-badge">✓</span>}
                      </div>
                      {m.matchId && (
                        <Link to={`/matches/${m.matchId}`} className="match-link">
                          View Match →
                        </Link>
                      )}
                      {m.team1Id && m.team2Id && !m.matchId && (
                        <span className="match-tbd">TBD</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="bracket-legend">
            <span className="legend-item"><span className="legend-bye">BYE</span> = automatic advancement</span>
            <span className="legend-item"><span className="legend-winner">✓</span> = match winner</span>
          </div>
        </div>
      )}
    </div>
  );
}
